import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

export default function PropertyDetail() {
  const { address: encoded } = useParams();
  const { state } = useLocation();
  const address = decodeURIComponent(encoded);

  const [propertyData, setPropertyData] = useState(null);
  const [owners, setOwners] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [bankName, setBankName] = useState(null);

  const balance = propertyData?.balance ?? state?.balance ?? 28050;
  const prepayments = propertyData?.prepayments ?? state?.prepayments ?? 0;
  const depositsHeld = propertyData?.depositsHeld ?? state?.depositsHeld ?? 0;
  const rent = propertyData?.rent ?? state?.rent ?? 1100;

  const [activeTab, setActiveTab] = useState('Summary');
  const mainTabs = ['Summary', 'Financials', 'Tenants', 'Communications', 'Tasks', 'Files', 'Notes', 'Event history'];

  const [activeSub, setActiveSub] = useState('Ledger');
  const subTabs = ['Ledger', 'Deposits & Prepayments', 'Rent', 'Recurring transactions', 'Payment settings'];

  const ledgerItems = useMemo(() => {
    const items = [];
    let bal = balance;
    for (let i = 0; i < 40; i++) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const dateStr = d.toLocaleDateString('en-US');
      const isRent = i % 2 === 0;
      const amount = isRent ? 1100 : 110;
      bal -= amount;
      items.push({
        date: dateStr,
        invoice: isRent ? 'Rental Income' : 'Late Fee Income',
        account: isRent ? 'Rent' : 'Late fee',
        transaction: 'Charge',
        amount: `$${amount.toFixed(2)}`,
        balance: `$${bal.toLocaleString()}`,
      });
    }
    return items;
  }, [balance]);

  useEffect(() => {
    async function fetchPropertyDetails() {
      try {
        const docRef = doc(db, 'properties', encoded);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setPropertyData(data);

          if (data.owners && Array.isArray(data.owners)) {
  const ownerNames = await Promise.all(data.owners.map(async (owner) => {
    if (typeof owner === 'string') {
      const ownerSnap = await getDoc(doc(db, 'users', owner));
      return ownerSnap.exists() ? ownerSnap.data().name : 'Unknown';
    } else if (typeof owner === 'object' && owner.name) {
      return owner.name;
    } else {
      return 'Unknown';
    }
  }));
  setOwners(ownerNames);
}

          if (data.tenant) {
            const tenantSnap = await getDoc(doc(db, 'users', data.tenant));
            setTenant(tenantSnap.exists() ? tenantSnap.data().name : '');
          }

        if (data.bankAccount) {
  const bankSnap = await getDoc(doc(db, 'bankAccounts', data.bankAccount));
  if (bankSnap.exists()) {
    const bankData = bankSnap.data();
    setBankName(`${bankData.name} • ****${bankData.last4}`);
  }
}
        }
      } catch (err) {
        console.error('Error fetching property data:', err);
      }
    }
    fetchPropertyDetails();
  }, [encoded]);

  return (
    <div style={styles.container}>
      <div style={styles.main}>
        <h1 style={styles.header}>
          {propertyData?.name || propertyData?.street || address} {tenant ? '– ' + tenant : ''}
        </h1>

        <div style={styles.buttonGroup}>
          <button style={styles.actionBtn}>Add addendum</button>
          <button style={styles.actionBtn}>Renew lease</button>
          <button style={styles.actionBtn}>End lease</button>
          <button style={styles.actionBtnDestructive}>Delete lease</button>
        </div>

        <nav style={styles.tabs}>
          {mainTabs.map(tab => (
            <div key={tab} onClick={() => setActiveTab(tab)} style={{ ...styles.tab, ...(activeTab === tab ? styles.activeTab : {}) }}>
              {tab}
            </div>
          ))}
        </nav>

        <div style={styles.tabContent}>
          {activeTab === 'Summary' && (
            <>
              <section style={styles.section}>
                <h3 style={styles.sectionTitle}>Lease details</h3>
                <table style={styles.detailsTable}>
                  <thead>
                    <tr>
                      <th style={styles.th}>UNIT</th>
                      <th style={styles.th}>RENTAL OWNER</th>
                      <th style={styles.th}>TENANT</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={styles.td}><Link to="#" style={styles.link}>{propertyData?.name || propertyData?.street || address}</Link></td>
                       <td style={styles.td}>
  <Link to="#" style={styles.link}>
    {owners.length > 0 ? owners.join(' · ') : 'Loading...'}
  </Link>
</td>
                      <td style={styles.td}><Link to="#" style={styles.link}>{tenant || '—'}</Link></td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section style={styles.section}><h3 style={styles.sectionSubtitle}>eSignature documents <Link to="#" style={styles.linkSmall}>Add addendum</Link></h3><p style={styles.emptyText}>You don’t have any eSignature documents for this lease right now.</p></section>
              <section style={styles.section}><h3 style={styles.sectionSubtitle}>Comments <Link to="#" style={styles.linkSmall}>Add</Link></h3><p style={styles.emptyText}>—</p></section>
              <section style={styles.section}><h3 style={styles.sectionSubtitle}>Renters insurance policies <Link to="#" style={styles.linkSmall}>Add</Link></h3><p>Uninsured residents: {tenant || '—'}</p></section>
              <section style={styles.section}><h3 style={styles.sectionSubtitle}>Custom fields</h3><button style={styles.outlineBtnSmall}>Add custom field</button><p style={styles.emptyText}>You do not have any custom fields created to track additional information in this location.</p></section>
              <section style={styles.section}><h3 style={styles.sectionSubtitle}>Pets</h3><p style={styles.emptyText}>No pets added.</p></section>
              <section style={styles.section}><h3 style={styles.sectionSubtitle}>Recent Notes</h3><ul style={styles.list}><li>No recent notes.</li></ul></section>
              <section style={styles.section}><h3 style={styles.sectionSubtitle}>Recent Files</h3><ul style={styles.list}><li>No recent files.</li></ul></section>
            </>
          )}

          {activeTab === 'Financials' && (
            <>
              <div style={styles.subNav}>
                {subTabs.map(st => (
                  <div key={st} onClick={() => setActiveSub(st)} style={{ ...styles.subTab, ...(activeSub === st ? styles.activeSubTab : {}) }}>
                    {st}
                  </div>
                ))}
              </div>

              {activeSub === 'Ledger' && (
                <>
                  <div style={styles.ledgerHeader}>
                    <div style={styles.toggleContainer}>
                      <input type="checkbox" id="newLedger" style={{ marginRight: 6 }} />
                      <label htmlFor="newLedger">New ledger view</label>
                    </div>
                  </div>

                  <div style={styles.ledgerControls}>
                    <div style={styles.viewButtons}>
                      <button style={styles.outlineBtnActive}>By date</button>
                      <button style={styles.outlineBtn}>By account</button>
                    </div>
                  </div>

                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          {['Date', 'Invoice', 'Account', 'Transaction', 'Amount', 'Balance'].map(h => (
                            <th key={h} style={styles.th}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerItems.map((row, i) => (
                          <tr key={i}>
                            <td style={styles.td}>{row.date}</td>
                            <td style={styles.td}>{row.invoice}</td>
                            <td style={styles.td}>{row.account}</td>
                            <td style={styles.td}>{row.transaction}</td>
                            <td style={styles.td}>{row.amount}</td>
                            <td style={styles.td}>{row.balance}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {activeSub !== 'Ledger' && <p style={styles.placeholder}>Content for “{activeSub}” coming soon…</p>}
            </>
          )}

          {activeTab !== 'Summary' && activeTab !== 'Financials' && <p style={styles.placeholder}>Content for “{activeTab}” coming soon…</p>}
        </div>
      </div>

      <div style={styles.sidebar}>
  <div style={styles.card}>
    <div style={styles.balanceRow}><span style={styles.balanceLabel}>Balance:</span><span style={styles.balanceValue}>${balance.toLocaleString()}.00</span></div>
    <div style={styles.balanceRow}><span>Prepayments:</span><span>${prepayments.toLocaleString()}.00</span></div>
    <div style={styles.balanceRow}><span>Deposits held:</span><span>${depositsHeld.toLocaleString()}.00</span></div>
    <div style={styles.separator} />
    <div style={styles.balanceRow}><span>Rent:</span><span>${rent.toLocaleString()}.00</span></div>
    <p style={styles.infoText}>Payment is due on the 1st of the month. If payment isn’t received, a one-time fee equal to 10% of recurring charges will be charged on the 7th of each month.</p>
    <button style={styles.primaryBtn}>Receive payment</button>
    <Link to="#" style={styles.ledgerLink}>Lease ledger</Link>
  </div>

  <div style={styles.card2}>
    <h4 style={{ marginTop: 0, marginBottom: 8 }}>Banking details</h4>
    <div style={styles.balanceRow}><span>Operating account:</span><span>{bankName || '—'}</span></div>
    <div style={styles.balanceRow}><span>Deposit trust account:</span><span><Link to="#" style={styles.linkSmall}>Setup</Link></span></div>
    <div style={styles.balanceRow}><span>Property reserve:</span><span>$0.00</span></div>
  </div>
</div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', gap: 24, padding: 24 },
  main: { flex: 3 },
  sidebar: { flex: 1 },
  header: { margin: 0, fontSize: 24 },
  buttonGroup: { margin: '16px 0', display: 'flex', gap: 8 },
  actionBtn: {
    padding: '8px 12px', borderRadius: 4, border: '1px solid #ccc',
    background: '#fff', cursor: 'pointer'
  },
  actionBtnDestructive: {
    padding: '8px 12px', borderRadius: 4, border: '1px solid #e00',
    background: '#fee', color: '#a00', cursor: 'pointer'
  },
    ledgerControls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 40,  
    marginBottom: 20,     // space above
    gap: 16    ,
       width: 'calc(100% + 300px)',
   marginRight: '-300px',          // optional spacing between groups
  },

  viewButtons: {
    display: 'flex',
    gap: 8,
    // remove its own top margin if any
  },

  actionButtons: {
    display: 'flex',
    gap: 12,
    // remove its own top margin if any
  },
  tabs: { display: 'flex', borderBottom: '1px solid #ddd' },
  tab: { padding: '12px 16px', cursor: 'pointer', color: '#555' },
  activeTab: {
    borderBottom: '2px solid #000', fontWeight: 'bold', color: '#000'
  },
  tabContent: { padding: '16px 0' },
  section: { marginBottom: 24 },
  sectionTitle: { marginBottom: 8 },
  sectionSubtitle: {
    marginBottom: 8, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8
  },
  detailsTable: { width: '100%', borderCollapse: 'collapse', marginBottom: 16 },
  th: {
    textAlign: 'left', padding: 8, borderBottom: '2px solid #ccc',
    position: 'sticky', top: 0, background: '#fff', zIndex: 1
  },
  td: { padding: 8, borderBottom: '1px solid #eee' },
  link: { color: '#06c', textDecoration: 'none' },
  linkSmall: { color: '#06c', fontSize: 14, textDecoration: 'none' },
  outlineBtnSmall: {
    padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4,
    background: '#fff', cursor: 'pointer', fontSize: 14, marginBottom: 8
  },
  emptyText: { color: '#888', fontStyle: 'italic' },
  list: { paddingLeft: 20, margin: 0 },
  placeholder: { color: '#888', fontStyle: 'italic', marginTop: 24 },

  /* Financials */
  subNav: { display: 'flex', gap: 24, marginTop: 16, borderBottom: '1px solid #eee', paddingBottom: 8 },
  subTab: { cursor: 'pointer', paddingBottom: 8, color: '#555' },
  activeSubTab: { borderBottom: '2px solid #2a9d8f', color: '#2a9d8f', fontWeight: 'bold' },
  ledgerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 },
  toggleContainer: { display: 'flex', alignItems: 'center', color: '#555' },
  balanceCard: { background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '12px 16px' },
  balanceCardTitle: { fontSize: 14, color: '#555' },
  balanceCardValue: { fontSize: 20, fontWeight: 'bold' },
  viewButtons: { display: 'flex', gap: 8, marginTop: 16 },
  outlineBtnActive: {
    padding: '6px 12px', border: '1px solid #2a9d8f', background: '#eafded',
    borderRadius: 4, cursor: 'pointer', color: '#2a9d8f'
  },
  outlineBtn: {
    padding: '6px 12px', border: '1px solid #ccc', background: '#fff',
    borderRadius: 4, cursor: 'pointer', color: '#555'
  },
  actionButtons: { display: 'flex', gap: 12, marginTop: 16 },
  primaryBtnGreen: {
    padding: '8px 16px', background: '#2a9d8f', color: '#fff',
    border: 'none', borderRadius: 4, cursor: 'pointer'
  },
  filterRow: { display: 'flex', alignItems: 'center', gap: 16, marginTop: 24 },
  select: { padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc' },
  matchCount: { fontSize: 14, color: '#555' },
 tableContainer: {
   maxHeight: 400,
   overflowY: 'auto',
   /* expand into the sidebar’s 300px */
   width: 'calc(100% + 335px)',
   marginRight: '-300px',
 },  
table: { width: '100%', borderCollapse: 'collapse' },

  /* Sidebar */
  card: {
    background: '#f5fcff', border: '1px solid #cce', borderRadius: 8, padding: 16
  },
  card2: {
    background: '#f5fcff', border: '1px solid #cce', borderRadius: 8, padding: 16, marginTop: 20
  },
  balanceRow: {
    display: 'flex', justifyContent: 'space-between', marginBottom: 8
  },
  balanceLabel: { fontWeight: 'bold' },
  balanceValue: { fontWeight: 'bold' },
  separator: { height: 1, background: '#cce', margin: '12px 0' },
  infoText: { fontSize: 14, lineHeight: 1.4, marginBottom: 12 },
  primaryBtn: {
    display: 'block', width: '100%', padding: 8, borderRadius: 4,
    border: 'none', background: '#007bff', color: '#fff', cursor: 'pointer', marginBottom: 8
  },
  ledgerLink: { display: 'block', textAlign: 'center', color: '#06c', textDecoration: 'none' }
};